import type { FileRejection } from 'react-dropzone';
import { arrayWithoutDuplicates } from '@/utils/helpers/commonHelpers';
import {
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_VIDEO_EXTENSIONS,
  SUPPORTED_DOCUMENT_EXTENSIONS,
  SUPPORTED_SCAN_EXTENSIONS,
  SUPPORTED_VOLUME_EXTENSIONS,
  MEDIA_MAX_SIZE_BYTES,
  IMediaCategory,
  MEDIA_CATEGORIES,
} from '@/consts/myMedia';

/**
 *
 * @param file
 * checks again the 3 files types defined by the backend
 * @returns
 */
const isFileSupportedImage = (file: File): boolean => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  return allowedTypes.includes(file.type);
};

const checkFileSize = (
  file: File,
  maxSizeInMB: number,
): boolean => {
  if (file.size > maxSizeInMB * 1024 * 1024) {
    return false;
  }
  return true;
};

/**
 * Decide how many decimal places to show when formatting a file size.
 *
 * Rules:
 * - If the value is >= 100 (e.g. 250 MB), or if the unit is bytes (i === 0),
 *   → show 0 decimals (e.g. "250 MB", "512 B").
 * - If the value is >= 10 but < 100 (e.g. 15.3 KB),
 *   → show 1 decimal (e.g. "15.3 KB").
 * - Otherwise (value < 10, e.g. 2.34 MB),
 *   → show 2 decimals (e.g. "2.34 MB").
 *
 * @param val The numeric value after converting bytes into the chosen unit.
 * @param i   The index of the unit in the sizes array (0 = B, 1 = KB, 2 = MB, ...).
 * @returns   Number of decimal places to use when formatting.
 *
 * @example
 * getDecimalPlaces(500, 0);   // 0 → "500 B"
 * getDecimalPlaces(2, 1);     // 2 → "2.00 KB"
 * getDecimalPlaces(15, 1);    // 1 → "15.0 KB"
 * getDecimalPlaces(250, 2);   // 0 → "250 MB"
 * getDecimalPlaces(9.56, 2);  // 2 → "9.56 MB"
 */
const getDecimalPlaces = (val: number, i: number): number => {
  if (val >= 100 || i === 0) return 0;
  if (val >= 10) return 1;
  return 2;
};

/**
 *
 * @param bytes takes in number of bytes
 * @returns human readable size like 2MB
 */
function bytesToHumanReadableSize(bytes: number = 0) {
  if (bytes === 0 || !bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const val = bytes / k ** i;

  const decimals = getDecimalPlaces(val, i);
  return `${val.toFixed(decimals)} ${sizes[i]}`;
}

function filesWithoutDuplicates(arr: File[]) {
  // Unique key: name + size + lastModified
  const key = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

  // Map ensures each key is stored once (later duplicates overwrite earlier)
  const map = new Map<string, File>();

  // Add files to map using the key
  arr.forEach((f) => map.set(key(f), f));

  // Return unique files
  return Array.from(map.values());
}

function mapDropzoneRejectionsToMessages(rejected: FileRejection[], maxSize: number) {
  const msgs: string[] = [];
  rejected.forEach(({ file, errors }) => {
    errors.forEach((e) => {
      if (e.code === 'file-too-large') {
        msgs.push(
          `"${file.name}" is too large (${bytesToHumanReadableSize(
            file.size,
          )}). Max allowed is ${bytesToHumanReadableSize(maxSize)} per file.`,
        );
      } else if (e.code === 'file-invalid-type') {
        msgs.push(`"${file.name}" has an invalid file type.`);
      } else {
        msgs.push(`"${file.name}": ${e.message}`);
      }
    });
  });
  return arrayWithoutDuplicates(msgs);
}

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.toLowerCase().split('.');
  // Handle double extensions like .nii.gz
  if (parts.length >= 3 && parts[parts.length - 2] === 'nii' && parts[parts.length - 1] === 'gz') {
    return 'nii.gz';
  }
  return parts.length > 1 ? parts[parts.length - 1] : '';
};

/**
 * Determine the category of a file based on its extension
 */
export const getFileCategory = (filename: string): IMediaCategory => {
  const ext = getFileExtension(filename);

  if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
    return MEDIA_CATEGORIES.IMAGE;
  }
  if (SUPPORTED_VIDEO_EXTENSIONS.includes(ext)) {
    return MEDIA_CATEGORIES.VIDEO;
  }
  if (SUPPORTED_DOCUMENT_EXTENSIONS.includes(ext)) {
    return MEDIA_CATEGORIES.DOCUMENT;
  }
  if (SUPPORTED_SCAN_EXTENSIONS.includes(ext)) {
    return MEDIA_CATEGORIES.SCAN;
  }
  if (SUPPORTED_VOLUME_EXTENSIONS.includes(ext)) {
    return MEDIA_CATEGORIES.VOLUME;
  }

  return MEDIA_CATEGORIES.OTHER;
};

/**
 * Get the maximum allowed size for a file
 */
export const getMaxSizeForFile = (file: File): number => {
  const category = getFileCategory(file.name);
  return MEDIA_MAX_SIZE_BYTES[category];
};

/**
 * Check if a file extension is supported
 */
export const isSupportedExtension = (filename: string): boolean => {
  const ext = getFileExtension(filename);
  const allSupported = [
    ...SUPPORTED_IMAGE_EXTENSIONS,
    ...SUPPORTED_VIDEO_EXTENSIONS,
    ...SUPPORTED_DOCUMENT_EXTENSIONS,
    ...SUPPORTED_SCAN_EXTENSIONS,
    ...SUPPORTED_VOLUME_EXTENSIONS,
  ];
  return allSupported.includes(ext);
};

/**
 * Validate a single file
 */
export interface FileValidationError {
  file: File;
  errors: string[];
}

export const validateFile = (file: File): string[] => {
  const errors: string[] = [];
  const ext = getFileExtension(file.name);

  // Check if extension is supported
  if (!isSupportedExtension(file.name)) {
    errors.push(`File type ".${ext}" is not supported`);
  }

  // Check file size
  const maxSize = getMaxSizeForFile(file);
  if (file.size > maxSize) {
    const category = getFileCategory(file.name);
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    errors.push(
      `File size (${fileSizeMB} MB) exceeds maximum allowed size for ${category} files (${maxSizeMB} MB)`,
    );
  }

  return errors;
};

/**
 * Validate multiple files
 */
export const validateFiles = (files: File[]): FileValidationError[] => {
  const validationErrors: FileValidationError[] = [];

  files.forEach((file) => {
    const errors = validateFile(file);
    if (errors.length > 0) {
      validationErrors.push({ file, errors });
    }
  });

  return validationErrors;
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (validationErrors: FileValidationError[]): string[] => validationErrors.flatMap(({ file, errors }) => errors.map((error) => `${file.name}: ${error}`));

/**
 * Get all supported extensions as a string for display
 */
export const getSupportedExtensionsString = (): string => {
  const allExtensions = [
    ...SUPPORTED_IMAGE_EXTENSIONS,
    ...SUPPORTED_VIDEO_EXTENSIONS,
    ...SUPPORTED_DOCUMENT_EXTENSIONS,
    ...SUPPORTED_SCAN_EXTENSIONS,
    ...SUPPORTED_VOLUME_EXTENSIONS,
  ];
  return allExtensions.map((ext) => `.${ext}`).join(', ');
};

/**
 * Get accept object for react-dropzone based on supported extensions
 */
export const getDropzoneAcceptObject = (): Record<string, string[]> => {
  const accept: Record<string, string[]> = {};

  // Images
  SUPPORTED_IMAGE_EXTENSIONS.forEach((ext) => {
    const mimeType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    if (!accept[mimeType]) accept[mimeType] = [];
    accept[mimeType].push(`.${ext}`);
  });

  // Videos
  SUPPORTED_VIDEO_EXTENSIONS.forEach((ext) => {
    const mimeType = `video/${ext}`;
    if (!accept[mimeType]) accept[mimeType] = [];
    accept[mimeType].push(`.${ext}`);
  });

  // Documents
  const docMimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    csv: 'text/csv',
  };

  SUPPORTED_DOCUMENT_EXTENSIONS.forEach((ext) => {
    const mimeType = docMimeTypes[ext] || `application/${ext}`;
    if (!accept[mimeType]) accept[mimeType] = [];
    accept[mimeType].push(`.${ext}`);
  });

  // Medical scans and volumes - use generic mime types
  [...SUPPORTED_SCAN_EXTENSIONS, ...SUPPORTED_VOLUME_EXTENSIONS].forEach((ext) => {
    const mimeType = 'application/octet-stream';
    if (!accept[mimeType]) accept[mimeType] = [];
    accept[mimeType].push(`.${ext}`);
  });

  return accept;
};

/**
 * Check if any files in the array will be converted to GLB
 */
export const hasConvertibleVolumes = (files: File[]): boolean => files.some((file) => {
  const ext = getFileExtension(file.name);
  return ext === 'obj' || ext === 'stl';
});

export {
  isFileSupportedImage,
  checkFileSize,
  bytesToHumanReadableSize,
  filesWithoutDuplicates,
  mapDropzoneRejectionsToMessages,
};
